import moment from "moment";

/**
 * Formats a list of dates into a human-readable smart string.
 * Supports consecutive ranges, specific individual dates, mixed months/years.
 * 
 * Examples:
 * - ["2026-03-22", "2026-03-23"] -> "22, 23 Mar 2026"
 * - "2026-03-22, 2026-03-23, 2026-03-24" -> "22 - 24 Mar 2026"
 * - Mixed: "22, 23 Jan & 15 Jul 2026"
 * 
 * @param {string|string[]} datesInput - Comma-separated string or array of dates
 * @param {string} timeStr - Optional time string to append (e.g. "10:00 AM")
 * @returns {string}
 */
export function formatSmartDates(datesInput, timeStr = "") {
  if (!datesInput) return "";

  const getYearFromText = (value) => {
    if (!value) return null;
    const match = String(value).match(/\b(19|20)\d{2}\b/);
    return match ? Number(match[0]) : null;
  };

  const getMonthFromText = (value) => {
    if (!value) return null;
    const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
    const str = String(value).toLowerCase();
    for (let i = 0; i < months.length; i++) {
      if (str.includes(months[i])) return i;
    }
    return null;
  };

  const parseFlexibleDate = (value, fallbackYear = null, fallbackMonth = null) => {
    if (!value) {
      return moment.invalid();
    }

    const trimmedValue = String(value).trim();
    const strictDate = moment(
      trimmedValue,
      [
        "DD MMM YYYY",
        "D MMM YYYY",
        "DD/MM/YYYY",
        "D/M/YYYY",
        "DD/MM/YY",
        "D/M/YY",
        "YYYY-MM-DD",
        "DD-MM-YYYY",
        "D-M-YYYY",
        "DD-MM-YY",
        "D-M-YY",
        "YYYY/MM/DD",
      ],
      true
    );

    if (strictDate.isValid()) {
      return strictDate;
    }

    const partialDate = moment(trimmedValue, ["DD MMM", "D MMM", "DD/MM", "D/M", "DD-MM", "D-M"], true);

    if (partialDate.isValid()) {
      if (fallbackYear) {
        partialDate.year(fallbackYear);
      }
      return partialDate;
    }

    if (/^\d{1,2}$/.test(trimmedValue) && fallbackMonth !== null && fallbackYear !== null) {
      const dayNum = Number(trimmedValue);
      if (dayNum >= 1 && dayNum <= 31) {
        const m = moment({ year: fallbackYear, month: fallbackMonth, day: dayNum });
        if (m.isValid()) return m;
      }
    }

    return moment.invalid();
  };

  const cleanedInput =
    typeof datesInput === "string"
      ? datesInput
          .replace(/\s*\|\s*\d{1,2}:\d{2}(?::\d{2})?\s*[APap][Mm]\s*$/, "")
          .replace(/\s*,\s*\d{1,2}:\d{2}(?::\d{2})?\s*[APap][Mm]\s*$/, "")
          .trim()
      : datesInput;

  let rawDates = [];
  if (Array.isArray(cleanedInput)) {
    rawDates = cleanedInput;
  } else if (typeof cleanedInput === "string") {
    if (cleanedInput.includes(" to ")) {
      const parts = cleanedInput.split(" to ");
      const startStr = parts[0].trim();
      const endStr = parts[1].trim();
      const startYear = getYearFromText(startStr);
      const endYear = getYearFromText(endStr);
      const start = parseFlexibleDate(startStr, endYear || startYear);
      const end = parseFlexibleDate(endStr, startYear || endYear);
      
      if (start.isValid() && end.isValid()) {
        if (!startYear && endYear) {
          start.year(endYear);
        }

        if (!endYear && startYear) {
          end.year(startYear);
        }

        let timePart = "";
        if (timeStr) {
          let formattedTime = timeStr;
          if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(timeStr)) {
            formattedTime = moment(timeStr, ["HH:mm:ss", "HH:mm", "h:mm A"]).format("h:mm A");
          }
          timePart = ` | ${formattedTime}`;
        }
        
        if (start.isSame(end, 'day')) {
          return `${start.format("DD MMM YYYY")}${timePart}`;
        } else if (start.year() === end.year()) {
          return `${start.format("DD MMM")} to ${end.format("DD MMM YYYY")}${timePart}`;
        } else {
          return `${start.format("DD MMM YYYY")} to ${end.format("DD MMM YYYY")}${timePart}`;
        }
      }
    } else {
      rawDates = cleanedInput
        .split(/[,|&]|\band\b/i)
        .map(d => d.trim())
        .filter(Boolean);
    }
  }

  const parsedTokenMeta = rawDates.map((val) => {
    const year = getYearFromText(val);
    const month = getMonthFromText(val);
    return { val, year, month };
  });

  let runningYear = parsedTokenMeta.map((t) => t.year).filter((y) => y !== null).pop() || null;
  let runningMonth = parsedTokenMeta.map((t) => t.month).filter((m) => m !== null).pop() || null;

  for (let i = parsedTokenMeta.length - 1; i >= 0; i--) {
    if (parsedTokenMeta[i].year !== null) {
      runningYear = parsedTokenMeta[i].year;
    } else {
      parsedTokenMeta[i].year = runningYear;
    }

    if (parsedTokenMeta[i].month !== null) {
      runningMonth = parsedTokenMeta[i].month;
    } else {
      parsedTokenMeta[i].month = runningMonth;
    }
  }

  let dateObjs = [];
  parsedTokenMeta.forEach(({ val, year, month }) => {
    const rangeMatch = String(val).match(/^(\d{1,2})\s*-\s*(\d{1,2})$/);
    if (rangeMatch && month !== null && year !== null) {
      const startDay = Number(rangeMatch[1]);
      const endDay = Number(rangeMatch[2]);
      if (startDay <= endDay && startDay >= 1 && endDay <= 31) {
        for (let d = startDay; d <= endDay; d++) {
          const m = moment({ year, month, day: d });
          if (m.isValid()) dateObjs.push(m);
        }
        return;
      }
    }

    const m = parseFlexibleDate(val, year, month);
    if (m.isValid()) {
      dateObjs.push(m);
    }
  });

  if (dateObjs.length === 0) return cleanedInput;

  dateObjs.sort((a, b) => a.unix() - b.unix());

  const seen = new Set();
  dateObjs = dateObjs.filter(m => {
    const s = m.format("YYYY-MM-DD");
    if (seen.has(s)) return false;
    seen.add(s);
    return true;
  });

  const groups = [];
  dateObjs.forEach(m => {
    const key = m.format("YYYY-MM");
    let group = groups.find(g => g.key === key);
    if (!group) {
      group = { key, year: m.year(), month: m.month(), days: [] };
      groups.push(group);
    }
    group.days.push(m.date());
  });

  const formattedGroups = groups.map(group => {
    const days = group.days.sort((a, b) => a - b);
    const dayParts = [];
    let start = days[0];
    let prev = days[0];

    for (let i = 1; i <= days.length; i++) {
      const current = days[i];
      if (current === prev + 1) {
        prev = current;
      } else {
        if (start === prev) {
          dayParts.push(start.toString());
        } else {
          dayParts.push(`${start} - ${prev}`);
        }
        start = current;
        prev = current;
      }
    }

    const monthName = moment().month(group.month).format("MMM");
    const dayStr = dayParts.length > 1
      ? dayParts.slice(0, -1).join(", ") + " & " + dayParts[dayParts.length - 1]
      : dayParts[0].toString();

    return { dayStr, monthName, year: group.year };
  });

  let result = "";
  formattedGroups.forEach((g, i) => {
    const isLast = i === formattedGroups.length - 1;
    const next = formattedGroups[i + 1];

    result += `${g.dayStr} ${g.monthName}`;

    if (isLast || (next && next.year !== g.year)) {
      result += ` ${g.year}`;
    }

    if (!isLast) {
      result += (formattedGroups.length > 2 && i < formattedGroups.length - 2) ? ", " : " & ";
    }
  });

  if (timeStr) {
    let formattedTime = timeStr;
    if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(timeStr)) {
        formattedTime = moment(timeStr, ["HH:mm:ss", "HH:mm", "h:mm A"]).format("h:mm A");
    }
    result += ` | ${formattedTime}`;
  }

  return result;
}
